import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Receipt, CreditCard, Check, AlertCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const TreasuryCheckFeeCard = ({ token, onFeePaid, exempt }) => {
  const [pendingFees, setPendingFees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [network, setNetwork] = useState('sepolia');
  const [fixedNetwork, setFixedNetwork] = useState(null);
  useEffect(() => {
    axios.get(`${API}/pas-treasury-check/issuance-network`)
      .then(r => { if (r.data.network) { setFixedNetwork(r.data.network); setNetwork(r.data.network); } }).catch(() => {});
  }, []);
  const [feeData, setFeeData] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    loadPendingFees();
  }, [token]);

  const loadPendingFees = async () => {
    try {
      const res = await axios.get(`${API}/pas-treasury-check/fee/pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingFees(res.data.pending_fees || []);
    } catch (err) {
      console.error('Failed to load pending fees:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateFee = async () => {
    const amountNum = parseFloat(amount);
    if (isNaN(amountNum) || amountNum <= 0) {
      toast.error('Enter a valid amount');
      return;
    }

    setCalculating(true);
    try {
      const res = await axios.post(`${API}/pas-treasury-check/fee/calculate`,
        { amount: amountNum, network },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFeeData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to calculate fee');
    } finally {
      setCalculating(false);
    }
  };

  const payFee = async () => {
    if (!feeData) {
      toast.error('Calculate fee first');
      return;
    }

    setPaying(true);
    try {
      const res = await axios.post(`${API}/pas-treasury-check/fee/checkout`,
        { amount: parseFloat(amount), network },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Redirecting to Stripe...');
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create checkout');
    } finally {
      setPaying(false);
    }
  };

  const confirmFee = async (feeId) => {
    try {
      const res = await axios.post(`${API}/pas-treasury-check/fee/confirm/${feeId}`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      loadPendingFees();
      onFeePaid?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to confirm fee');
    }
  };

  if (exempt) return (
    <div className="bg-slate-800 border border-emerald-600/50 rounded-xl p-6 self-start" data-testid="treasury-fee-exempt-card">
      <div className="flex items-center gap-3"><Check className="w-7 h-7 text-emerald-400" />
        <div><h3 className="text-lg font-bold text-white">Treasury Check Fee Waived</h3>
          <p className="text-sm text-slate-400">Administrator account: no platform fee or gas charges. Mint Treasury Checks directly.</p></div>
      </div>
    </div>
  );

  return (
    <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden" data-testid="treasury-fee-card">
      <div className="bg-gradient-to-r from-amber-600 to-orange-600 px-6 py-4">
        <div className="flex items-center gap-3">
          <Receipt className="w-8 h-8 text-white" />
          <div>
            <h3 className="text-lg font-bold text-white">Treasury Check BETH Gas Fee (1%)</h3>
            <p className="text-sm text-amber-100">1% BETH gas fee required before each check action</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Info Banner */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex gap-3">
          <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-200">
            <p className="font-medium">BETH Gas Fee Required</p>
            <p className="text-amber-300 mt-1">
              Each check action (mint, transfer, endorse, deposit, redeem) costs one <strong>1% BETH gas fee</strong> ($0.50 minimum), paid by card. Blockchain fees on Base are paid by the BETH Treasury Reserve.
              The platform handles all blockchain transactions from its funding wallet.
            </p>
          </div>
        </div>

        {/* Pending Fees */}
        {pendingFees.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-400" /> Paid Fees (Ready to Mint)
            </h4>
            <div className="space-y-2">
              {pendingFees.map((fee) => (
                <div key={fee.fee_id} className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-white font-medium">${fee.check_amount_usd.toLocaleString()} Treasury Check</p>
                    <p className="text-xs text-emerald-300">
                      Fee paid: ${(fee.total_fee_usd || fee.fee_usd || 0).toFixed(2)} · {fee.network}
                    </p>
                  </div>
                  <span className="px-3 py-1 bg-emerald-600 text-white text-xs rounded-full">
                    Ready to Mint
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Calculate & Pay Fee */}
        <div className="space-y-4 border-t border-slate-700 pt-6">
          <h4 className="text-sm font-medium text-slate-300 flex items-center gap-2">
            <CreditCard className="w-4 h-4" /> Pay Fee for New Check
          </h4>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Check Amount (USD)</label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                <input type="number" value={amount} onChange={(e) => { setAmount(e.target.value); setFeeData(null); }}
                  className="w-full pl-8 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                  placeholder="1000"
                  data-testid="fee-amount-input" />
              </div>
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Network</label>
              {fixedNetwork ? (
                <p className="px-4 py-2 bg-slate-700/60 border border-slate-600 rounded-lg text-white" data-testid="fee-network-fixed">
                  {({ sepolia: 'Sepolia (Test)', mainnet: 'Ethereum', base: 'Base', arbitrum: 'Arbitrum' })[fixedNetwork]}
                </p>
              ) : (
              <select value={network} onChange={(e) => { setNetwork(e.target.value); setFeeData(null); }}
                className="w-full px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white"
                data-testid="fee-network-select">
                <option value="sepolia">Sepolia (Test)</option>
                <option value="mainnet">Ethereum</option>
                <option value="base">Base</option>
                <option value="arbitrum">Arbitrum</option>
              </select>
              )}
            </div>
          </div>

          <button onClick={calculateFee}
            disabled={calculating || !amount}
            className="w-full px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm disabled:opacity-50"
            data-testid="fee-calculate-btn">
            {calculating ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'Calculate Fee'}
          </button>

          {feeData && (
            <div className="bg-slate-700/50 rounded-lg p-4 space-y-2" data-testid="fee-breakdown">
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Check Amount</span>
                <span className="text-white">${feeData.check_amount_usd?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-400">Platform Fee ({feeData.platform_fee_percent || feeData.fee_percent || 1}%)</span>
                <span className="text-amber-400">${(feeData.platform_fee_usd || feeData.fee_usd || 0).toFixed(2)}</span>
              </div>
              {feeData.gas_fee_usd !== undefined && (
                <div className="flex justify-between text-sm">
                  <span className="text-slate-400">Network gas</span>
                  <span className="text-blue-400">
                    Paid by BETH Treasury Reserve
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm font-medium border-t border-slate-600 pt-2">
                <span className="text-slate-300">Total Fee</span>
                <span className="text-white">${(feeData.total_fee_usd || feeData.fee_usd || 0).toFixed(2)}</span>
              </div>
              {feeData.eth_price_usd && (
                <p className="text-xs text-slate-500 text-right">ETH @ ${feeData.eth_price_usd.toLocaleString()}</p>
              )}

              <button onClick={payFee}
                disabled={paying}
                className="w-full mt-4 px-4 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="fee-pay-btn">
                {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
                Pay ${(feeData.total_fee_usd || feeData.fee_usd || 0).toFixed(2)} via Stripe
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TreasuryCheckFeeCard;
